import React, { useState, useEffect, useRef } from 'react';
import workflowAPI from '../services/api';
import './ArtifactsViewer.css';

// Get backend URL for editor (must include protocol)
const getBackendUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
  // Ensure it has a protocol
  if (!apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
    return `https://${apiUrl}`;
  }
  return apiUrl;
};

interface ArtifactsViewerProps {
  executionId: string;
  onApprove: (artifacts: any) => void;
  onReject: (reason?: string) => void;
}

const ArtifactsViewer: React.FC<ArtifactsViewerProps> = ({ executionId, onApprove, onReject }) => {
  const [artifacts, setArtifacts] = useState<any>(null);
  const [execution, setExecution] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [filesExist, setFilesExist] = useState(false);
  const [workflowType, setWorkflowType] = useState<'epic' | 'pr' | 'unknown'>('unknown');
  const filesExistRef = useRef(false);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    console.log(`🎨 ArtifactsViewer mounted for execution: ${executionId}`);
    fetchExecutionData();

    // Poll for artifacts and status updates every 2 seconds
    // For PR workflows: continue polling until workflow is completed OR we have PR data
    // For Epic workflows: stop when files exist
    pollIntervalRef.current = setInterval(() => {
      console.log(`📊 Polling - filesExist: ${filesExistRef.current}, workflowType: ${workflowType}, status: ${execution?.status}`);

      // For PR workflows, continue polling until completed
      if (workflowType === 'pr') {
        if (execution?.status !== 'completed' && execution?.status !== 'rejected' && execution?.status !== 'terminated') {
          console.log('🔄 PR workflow still running or pending approval, continuing to poll...');
          fetchExecutionData();
        } else {
          console.log('✅ PR workflow completed/terminated, stopping poll');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }
      // For Epic workflows, stop when files exist
      else if (!filesExistRef.current) {
        console.log('Polling for artifacts...');
        fetchExecutionData();
      } else {
        console.log('✅ Files exist, stopping poll');
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    }, 2000); // Poll every 2 seconds for faster updates

    return () => {
      console.log('🧹 Cleaning up ArtifactsViewer interval');
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]); // Only re-run when executionId changes

  const fetchExecutionData = async () => {
    try {
      console.log(`📦 Fetching execution data for: ${executionId}`);
      setLoading(true);

      // Fetch both execution details and artifacts
      const [execData, artifactsData] = await Promise.all([
        workflowAPI.getExecution(executionId),
        workflowAPI.getExecutionArtifacts(executionId)
      ]);

      setExecution(execData);
      setArtifacts(artifactsData);

      // Determine workflow type from execution data
      const workflowName = execData?.workflow_name || '';
      const stepData = execData?.stepData;

      // PR workflows: Code Implementation, SonarQube (has PR approval step), Unit Test
      if (workflowName.toLowerCase().includes('code') ||
          workflowName.toLowerCase().includes('implementation') ||
          workflowName.toLowerCase().includes('unit test') ||
          workflowName.toLowerCase().includes('sonar')) {
        console.log('🔍 Detected PR approval workflow');
        setWorkflowType('pr');

        // For PR workflows, ALWAYS set filesExist to true (we'll show "waiting for PR" if no data yet)
        setFilesExist(true);
        filesExistRef.current = true;

        // Don't stop polling based on PR data - keep polling until workflow completes
        // This ensures we get updated PR info and JIRA info as they become available
        console.log('🔄 PR workflow - will poll until workflow completes');
      } else {
        console.log('🔍 Detected epic approval workflow');
        setWorkflowType('epic');

        // Check if MD files exist for epic workflows
        if (artifactsData && (artifactsData.epicMd || artifactsData.storiesMd ||
            (artifactsData.epic && artifactsData.stories))) {
          console.log('🎉 Epic artifacts exist, stopping poll');
          setFilesExist(true);
          filesExistRef.current = true;
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        } else {
          console.log('⚠️  Epic artifacts not found yet, will keep polling');
          setFilesExist(false);
          filesExistRef.current = false;
        }
      }

      setLoading(false);
    } catch (err: any) {
      console.error('❌ Error fetching execution data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      // The backend will regenerate JSON from MD files before signaling the workflow
      onApprove({});
    } catch (err: any) {
      alert(`Error approving: ${err.message}`);
      setApproving(false);
    }
  };

  const handleOpenFile = (filename: string) => {
    // Get backend URL with proper protocol
    const backendUrl = getBackendUrl();

    // Get the access token from localStorage
    const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');

    if (!token) {
      alert('Authentication token not found. Please log in again.');
      return;
    }

    // Open the editor page with execution ID, filename, and token as parameters
    const editorUrl = `${backendUrl}/editor/editor.html?executionId=${executionId}&filename=${filename}&token=${encodeURIComponent(token)}`;

    // Open in a new window with specific dimensions
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      editorUrl,
      `editor-${filename}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  console.log('ArtifactsViewer render - filesExist:', filesExist, 'artifacts:', artifacts, 'workflowType:', workflowType);

  if (loading) {
    return <div className="artifacts-loading">Loading artifacts...</div>;
  }

  if (error) {
    return <div className="artifacts-error">Error: {error}</div>;
  }

  // Show waiting message if artifacts not ready
  if (!filesExist && workflowType === 'epic') {
    return <div className="artifacts-empty">No artifacts generated yet. Waiting for epic generation...</div>;
  }

  if (!filesExist && workflowType === 'pr') {
    return <div className="artifacts-empty">Waiting for PR creation...</div>;
  }

  // Extract PR data from execution stepData or outputData
  let prUrl = '';
  let prNumber: number | undefined;
  let jiraTicketId = '';
  let jiraTicketUrl = '';

  // The backend queries Temporal and stores the result in execution.stepData
  // The stepData object has an outputData field with progressive data (PR, JIRA, SonarQube)
  let parsedOutputData = execution?.stepData?.outputData || execution?.output_data || execution?.outputData;

  // Parse if it's a JSON string
  if (typeof parsedOutputData === 'string') {
    try {
      parsedOutputData = JSON.parse(parsedOutputData);
    } catch (err) {
      console.warn('Failed to parse outputData:', err);
      parsedOutputData = {};
    }
  }

  // Extract PR data
  if (parsedOutputData?.pullRequest) {
    prUrl = parsedOutputData.pullRequest.prUrl || '';
    prNumber = parsedOutputData.pullRequest.prNumber;
    console.log('✅ Found PR data:', { prUrl, prNumber });
  }

  // Extract JIRA data
  if (parsedOutputData?.jiraStory) {
    jiraTicketId = parsedOutputData.jiraStory.ticketId || '';
    jiraTicketUrl = parsedOutputData.jiraStory.ticketUrl || '';
    console.log('✅ Found JIRA data:', { jiraTicketId, jiraTicketUrl });
  }

  // Fallback to stepData if not in outputData
  if (execution?.stepData?.steps) {
    const createPrStep = execution.stepData.steps.find((s: any) => s.id === 'create-pr');
    const waitForImplStep = execution.stepData.steps.find((s: any) => s.id === 'wait-for-implementation');
    const prApprovalStep = execution.stepData.steps.find((s: any) => s.id === 'pr-approval');
    const createJiraStep = execution.stepData.steps.find((s: any) => s.id === 'create-jira-story');
    const updateJiraStep = execution.stepData.steps.find((s: any) => s.id === 'update-jira');

    // Get PR URL from step data or artifacts (if not already extracted from outputData)
    if (!prUrl) {
      prUrl = createPrStep?.data?.prUrl ||
              waitForImplStep?.data?.prUrl ||
              waitForImplStep?.data?.artifacts?.prUrl ||
              prApprovalStep?.data?.prUrl || '';

      prNumber = createPrStep?.data?.prNumber ||
                 waitForImplStep?.data?.prNumber ||
                 waitForImplStep?.data?.artifacts?.prNumber ||
                 prApprovalStep?.data?.prNumber;

      console.log('📊 Using PR data from stepData:', { prUrl, prNumber });
    }

    // Get JIRA data from create-jira-story step (when JIRA is first created)
    if (!jiraTicketId && createJiraStep?.data) {
      jiraTicketId = createJiraStep.data.jiraTicketId || createJiraStep.data.ticketId || '';
      jiraTicketUrl = createJiraStep.data.jiraTicketUrl || createJiraStep.data.ticketUrl || '';
      console.log('📊 Using JIRA data from create-jira-story step:', { jiraTicketId, jiraTicketUrl });
    }

    // Also check update-jira step (after PR is approved and workflow updates JIRA)
    if (!jiraTicketId && updateJiraStep?.data) {
      jiraTicketId = updateJiraStep.data.ticketId || '';
      jiraTicketUrl = updateJiraStep.data.ticketUrl || '';
      console.log('📊 Using JIRA data from update-jira step:', { jiraTicketId, jiraTicketUrl });
    }
  }

  // Also check execution parameters for JIRA ticket ID
  if (!jiraTicketId && execution?.parameters?.jiraTicketId) {
    jiraTicketId = execution.parameters.jiraTicketId;
  }

  // Check if workflow is completed
  const isCompleted = execution?.status === 'completed';
  const isPendingApproval = execution?.status === 'pending_approval';

  // Render PR Approval View
  if (workflowType === 'pr') {
    return (
      <div className="artifacts-viewer">
        <div className="artifacts-header">
          <h3>{isCompleted ? '✅ Workflow Completed' : '🔀 Pull Request Review'}</h3>
          <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            {isCompleted
              ? 'The pull request has been approved and the JIRA ticket has been updated.'
              : 'A pull request has been created for this user story. Please review the changes before approving.'}
          </p>
        </div>

        {/* PR Information Section - Show if we have prUrl OR if status is pending_approval */}
        {(prUrl || isPendingApproval) && (
          <div className="artifact-section" style={{
            backgroundColor: '#e7f3ff',
            border: '1px solid #2196F3',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ marginBottom: '1rem', color: '#1976D2' }}>
              📎 Pull Request {prNumber ? `#${prNumber}` : ''}
            </h4>
            {prUrl ? (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <a
                    href={prUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#1976D2',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      wordBreak: 'break-all'
                    }}
                  >
                    {prUrl}
                  </a>
                </div>
                <p style={{ fontSize: '0.9rem', color: '#555' }}>
                  Click the link above to review the code changes on GitHub.
                </p>
              </>
            ) : (
              <p style={{ fontSize: '0.9rem', color: '#555' }}>
                Pull request has been created. Check the execution logs or GitHub for the PR link.
              </p>
            )}
          </div>
        )}

        {/* Only show "waiting" message if NOT pending approval and no PR URL */}
        {!prUrl && !isPendingApproval && !isCompleted && (
          <div className="artifact-section" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ color: '#856404' }}>
              ⏳ Pull request is being created. Please wait...
            </p>
          </div>
        )}

        {/* JIRA Ticket Information (shown after approval and completion) */}
        {jiraTicketUrl && (
          <div className="artifact-section" style={{
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ marginBottom: '1rem', color: '#155724' }}>
              ✅ JIRA Ticket Updated
            </h4>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Ticket:</strong> {jiraTicketId}
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <a
                href={jiraTicketUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#155724',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textDecoration: 'underline'
                }}
              >
                View on JIRA →
              </a>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#155724' }}>
              The JIRA ticket has been updated with the PR link.
            </p>
          </div>
        )}

        {/* Instructions - Only show when pending approval */}
        {!isCompleted && (
          <div className="artifact-section">
            <h4>📋 Next Steps</h4>
            <ol style={{ paddingLeft: '1.5rem', lineHeight: '1.8' }}>
              <li>Click the PR link above to review the code changes</li>
              <li>Check the implementation matches the requirements</li>
              <li>Verify unit tests are included and passing</li>
              <li>Click <strong>Approve</strong> below to update the JIRA ticket</li>
            </ol>
          </div>
        )}

        {/* Approval Actions - Only show when pending approval */}
        {!isCompleted && (
          <div className="artifacts-actions">
            <button
              onClick={handleApprove}
              className="approve-btn"
              disabled={approving || (!prUrl && !isPendingApproval)}
            >
              {approving ? '⏳ Approving...' : '✅ Approve'}
            </button>
            <button
              onClick={() => onReject()}
              className="reject-btn"
              disabled={approving}
            >
              ❌ Reject
            </button>
          </div>
        )}
      </div>
    );
  }

  // Render Epic Approval View (original functionality)
  return (
    <div className="artifacts-viewer">
      <div className="artifacts-header">
        <h3>📦 Generated Artifacts</h3>
        <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
          Click on the file links below to open the editor in a new window.
          <br />
          Edit the content, save your changes, and then click <strong>Approve</strong> to continue the workflow.
        </p>
      </div>

      {/* File Links Section */}
      <div className="artifact-section">
        <h4>📄 Artifact Files</h4>
        <div className="file-links">
          <div className="file-link-item">
            <button
              onClick={() => handleOpenFile('epic.json')}
              className="file-link-btn"
              title="Click to open epic.json in the web editor"
            >
              📝 epic.json
            </button>
            <span className="file-description">Epic description and details (click to edit)</span>
          </div>

          <div className="file-link-item">
            <button
              onClick={() => handleOpenFile('stories.json')}
              className="file-link-btn"
              title="Click to open stories.json in the web editor"
            >
              📝 stories.json
            </button>
            <span className="file-description">All user stories (click to edit)</span>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      {artifacts && artifacts.epic && (
        <div className="artifact-section">
          <h4>📊 Summary</h4>
          <div className="artifact-summary">
            <p><strong>Epic:</strong> {artifacts.epic.title}</p>
            <p><strong>Stories:</strong> {artifacts.stories?.length || 0} user stories</p>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="artifact-section" style={{ backgroundColor: '#f0f8ff', padding: '1rem', borderRadius: '8px' }}>
        <h4>✏️ How to Edit</h4>
        <ol style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
          <li>Click on <strong>epic.md</strong> or <strong>stories.md</strong> above to open the file</li>
          <li>Edit the markdown content in your preferred editor</li>
          <li>Save your changes to the file</li>
          <li>Click <strong>Approve</strong> below to continue the workflow</li>
        </ol>
        <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
          💡 <strong>Note:</strong> When you click Approve, the JSON files will be automatically regenerated from your markdown edits.
        </p>
      </div>

      {/* Approval Actions */}
      <div className="artifacts-actions">
        <button
          onClick={handleApprove}
          className="approve-btn"
          disabled={approving}
        >
          {approving ? '⏳ Approving...' : '✅ Approve'}
        </button>
        <button
          onClick={() => onReject()}
          className="reject-btn"
          disabled={approving}
        >
          ❌ Reject
        </button>
      </div>
    </div>
  );
};

export default ArtifactsViewer;

